/**
 * Claim detail's "Excel + touches" workbook (review #968: SVPs and VPs asked for the
 * claims and their touches in one export, to analyse together).
 *
 * Two sheets, decided 2026-11-04 (Yehuda):
 * - **Claims**: exactly the Claim detail export, the rows and order on screen. Its
 *   Touches count links to that claim's first touch on the next sheet.
 * - **Touches**: every touch on those claims, the Touch detail export's columns, grouped
 *   by claim in the Claims sheet's order and by date within a claim. Its first column,
 *   the claim's visit, links back to the claim's row.
 *
 * Not one sheet with a row per touch: that repeats a claim's charge and remaining on
 * every touch, so any total or pivot over the sheet counts them several times.
 *
 * A claim is matched to its touches by visit + payer, any date, by anyone: the same key
 * the Touches column counts by (touchRows.ts), so a count and the rows it links to agree.
 */
import type { ExportColumn, XLSXSheet } from '@/ui/export'
import type { TouchDetailData } from '@/data/touchDetail'
import { claimKey, touchesByClaim } from '@/data/touchRows'

export const CLAIMS_SHEET = 'Claims'
export const TOUCHES_SHEET = 'Touches'

/** One row of the Touches sheet: a touch, and the Claims-sheet row it belongs to. */
interface TouchLine {
  touch: number
  claimRow: number
}

export function claimTouchBook<T>(opts: {
  claims: readonly T[]
  claimColumns: readonly ExportColumn<T>[]
  /** The claim's visit and payer name, its touch key. */
  visitOf: (claim: T) => string
  payerOf: (claim: T) => string
  touches: TouchDetailData
  touchColumns: readonly ExportColumn<number>[]
}): [XLSXSheet<T>, XLSXSheet<TouchLine>] {
  const { claims, visitOf, payerOf, touches } = opts
  const byClaim = touchesByClaim(touches)
  const date = (i: number) => touches.dict.d[touches.cols.d[i]!] ?? ''

  // Sheet row of each claim's first touch, and the touches in sheet order. A claim key
  // seen twice (the same visit and payer on two rows) lists its touches once, under
  // the first, and both rows link there.
  const firstTouchRow = new Map<string, number>()
  const lines: TouchLine[] = []
  claims.forEach((c, ci) => {
    const visit = visitOf(c)
    if (!visit) return
    const k = claimKey(visit, payerOf(c))
    if (firstTouchRow.has(k)) return
    const rows = byClaim.get(k)
    if (!rows?.length) return
    firstTouchRow.set(k, lines.length + 2)
    const sorted = [...rows].sort((a, b) => (date(a) < date(b) ? -1 : date(a) > date(b) ? 1 : a - b))
    for (const t of sorted) lines.push({ touch: t, claimRow: ci + 2 })
  })

  const countOf = (c: T) => {
    const visit = visitOf(c)
    return visit ? (byClaim.get(claimKey(visit, payerOf(c)))?.length ?? 0) : null
  }
  const linkOf = (c: T) => {
    const visit = visitOf(c)
    const at = visit ? firstTouchRow.get(claimKey(visit, payerOf(c))) : undefined
    return at === undefined ? null : `'${TOUCHES_SHEET}'!A${at}`
  }
  const touchesColumn: ExportColumn<T> = { header: 'Touches', value: countOf, link: linkOf }
  const hasTouches = opts.claimColumns.some((c) => c.header === 'Touches')
  const claimColumns = hasTouches
    ? opts.claimColumns.map((c) => (c.header === 'Touches' ? touchesColumn : c))
    : [...opts.claimColumns, touchesColumn]

  const touchColumns: ExportColumn<TouchLine>[] = [
    {
      header: 'Claim (visit)',
      value: (l) => touches.cols.v[l.touch] ?? '',
      link: (l) => `'${CLAIMS_SHEET}'!A${l.claimRow}`,
    },
    ...opts.touchColumns.map((c) => ({ header: c.header, value: (l: TouchLine) => c.value(l.touch) })),
  ]

  return [
    { name: CLAIMS_SHEET, rows: claims, columns: claimColumns },
    { name: TOUCHES_SHEET, rows: lines, columns: touchColumns },
  ]
}
